import { listOf, pathTo, report, type Place } from "./checks.ts";
import type { OnHit } from "./types.ts";

/**
 * An ability's cleanses (the author's ruling of 2026-10-02, ADR-079's note of the same date): the effects it ends on
 * each target the ability's hits land on, or, for an ability aimed at oneself or an ally, on the one it lands on (its
 * amendment of 2026-10-03), the one way, with an effect's own end and its bearer's death, that a damage over time stops.
 * Each is an effect the table holds, at most once, and never one the same ability lands, by `effects` or `onHit`, so a
 * landing never undoes itself.
 */

/**
 * Checks an ability's `cleanses`.
 *
 * @param place Where to report.
 * @param value The list.
 * @param effectIds The ids the effects table holds.
 * @param applied The ids the ability's `effects` lands, or null when they were wrong.
 * @param onHit The ability's chances on a hit, or null when they were wrong.
 * @returns The ids, or null.
 */
export function cleansesOf(place: Place, value: unknown, effectIds: ReadonlySet<string>, applied: readonly string[] | null, onHit: readonly OnHit[] | null): string[] | null {
  const list = listOf(place, value, "cleanses");
  if (list === null) {
    return null;
  }
  const landed = new Set([...(applied ?? []), ...(onHit ?? []).map((entry) => entry.effect)]);
  let ok = true;
  const cleansed: string[] = [];
  for (const [index, item] of list.entries()) {
    if (typeof item !== "string") {
      report(place, `${pathTo("cleanses", index, true)} is not a string`);
      ok = false;
    } else if (cleansed.includes(item)) {
      report(place, `cleanses names ${item} twice`);
      ok = false;
    } else if (!effectIds.has(item)) {
      report(place, `cleanses the effect ${item}, which the effects table does not hold`);
      ok = false;
    } else if (landed.has(item)) {
      report(place, `cleanses ${item}, which it lands itself`);
      ok = false;
    } else {
      cleansed.push(item);
    }
  }
  return ok ? cleansed : null;
}
